import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortableSensors } from "@/lib/dnd-sensors";
import { Plus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  FormDefinition,
  FormField,
  FormFieldKind,
  FormSection,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { queryKeys } from "@/lib/query-keys";
import { useAutosave, aggregateSaveStatus, type SaveStatus } from "@/hooks/use-autosave";
import type { AdminFormRow } from "./types";
import { SectionBlock } from "./section-block";
import { FieldConfigPanel } from "./field-config-panel";
import { useAddField } from "./use-add-field";
import { useUpdateField } from "./use-update-field";
import { useRemoveField } from "./use-remove-field";
import { useReorderFields } from "./use-reorder-fields";
import { useUpdateFormDefinition } from "./use-update-form-definition";

// Two-column builder canvas:
//   • Left  — sections + fields (DndContext + per-section SortableContext)
//   • Right — FieldConfigPanel for the selected field, or empty placeholder
//
// Per-field operations (add / update / remove / reorder) call the Phase 6
// per-field hooks which do Pattern C optimistic cache writes against
// adminForms.detail(id). The canvas reads the definition straight from
// the query cache — no local mirror.
//
// Section-level operations (heading rename, add section, delete section)
// don't have a per-section endpoint. They write a "section patch" through
// useAutosave (500ms debounce) that, at flush time, re-reads the cache and
// merges the patch into the latest definition. This protects against
// per-field optimistic writes that landed during the typing window getting
// clobbered by a stale closure.

export function FormBuilderCanvas({
  form,
  onSaveStatusChange,
}: {
  form: AdminFormRow;
  onSaveStatusChange?: (status: SaveStatus) => void;
}) {
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const definition = form.definition;
  const detailKey = useMemo(() => queryKeys.adminForms.detail(form.id), [form.id]);

  const addField = useAddField();
  const updateField = useUpdateField();
  const removeField = useRemoveField();
  const reorderFields = useReorderFields();
  const updateDefinition = useUpdateFormDefinition();

  // Section-level autosave: takes a function that produces the next
  // definition from the freshest cache snapshot, then dispatches a
  // whole-blob PATCH. The reducer pattern keeps per-field optimistic writes
  // in scope at flush time.
  type SectionMutator = (def: FormDefinition) => FormDefinition;
  const sectionAutosave = useAutosave<SectionMutator>({
    onFlush: async (mutator) => {
      const latest =
        queryClient.getQueryData<AdminFormRow>(detailKey)?.definition ?? definition;
      const next = mutator(latest);
      await updateDefinition.mutateAsync({ id: form.id, definition: next });
    },
    wait: 500,
  });

  // Compose the aggregate save status from the section autosave + every
  // mutation hook's isPending flag. Per-field mutations are short-lived;
  // surfacing them in the header keeps the indicator honest during the
  // network roundtrip even though the optimistic write is instant.
  const status: SaveStatus = aggregateSaveStatus(
    sectionAutosave.status,
    addField.isPending ||
      updateField.isPending ||
      removeField.isPending ||
      reorderFields.isPending
      ? "saving"
      : "idle",
  );

  // Notify the parent so the header save indicator can aggregate across
  // the 3 tab streams without duplicating the precedence logic. Effect
  // (not inline call) so we don't setState during render.
  useEffect(() => {
    onSaveStatusChange?.(status);
  }, [status, onSaveStatusChange]);

  // Resolve the selected field every render — cheaper than threading it
  // through the section/field tree.
  const { selectedField, selectedSectionId } = useMemo(() => {
    for (const section of definition.sections) {
      const found = section.fields.find((f) => f.id === selectedFieldId);
      if (found) return { selectedField: found, selectedSectionId: section.id };
    }
    return { selectedField: null, selectedSectionId: null };
  }, [definition, selectedFieldId]);

  // ----- Mutation helpers -------------------------------------------------

  const handleAddField = useCallback(
    (sectionId: string, kind: FormFieldKind, insertAfterFieldId?: string) => {
      const newField = makeField(kind, definition);
      addField.mutate(
        { formId: form.id, sectionId, field: newField },
        {
          onSuccess: () => {
            setSelectedFieldId(newField.id);
          },
        },
      );
      if (insertAfterFieldId) {
        // The Phase 6 add endpoint always appends. To support "insert after"
        // we dispatch a reorder right after the add lands. Use the optimistic
        // cache write that useAddField just applied to compute the desired
        // ordering — read from the cache rather than the closed-over
        // definition so the new field's UUID is present.
        setTimeout(() => {
          const latest = queryClient.getQueryData<AdminFormRow>(detailKey);
          if (!latest) return;
          const sections = latest.definition.sections.map((s) => {
            if (s.id !== sectionId) {
              return { id: s.id, fieldIds: s.fields.map((f) => f.id) };
            }
            const ids = s.fields.map((f) => f.id);
            const newIdx = ids.indexOf(newField.id);
            const afterIdx = ids.indexOf(insertAfterFieldId);
            if (newIdx === -1 || afterIdx === -1 || newIdx === afterIdx + 1) {
              return { id: s.id, fieldIds: ids };
            }
            const without = ids.filter((id) => id !== newField.id);
            const targetIdx = without.indexOf(insertAfterFieldId);
            without.splice(targetIdx + 1, 0, newField.id);
            return { id: s.id, fieldIds: without };
          });
          reorderFields.mutate({ formId: form.id, sections });
        }, 0);
      }
    },
    [addField, definition, detailKey, form.id, queryClient, reorderFields],
  );

  const handlePatchField = useCallback(
    (fieldId: string, patch: Partial<FormField>) => {
      const current = findField(definition, fieldId);
      if (!current) return;
      // Cast through unknown — Partial<FormField> on a discriminated union
      // loses the discriminator and TS can't prove the merged result is a
      // valid FormField. Runtime constraint enforced by formFieldSchema.
      const next = { ...current, ...patch } as unknown as FormField;
      updateField.mutate({ formId: form.id, fieldId, field: next });
    },
    [definition, form.id, updateField],
  );

  const handleDeleteField = useCallback(
    (fieldId: string) => {
      removeField.mutate({ formId: form.id, fieldId });
      if (selectedFieldId === fieldId) setSelectedFieldId(null);
    },
    [form.id, removeField, selectedFieldId],
  );

  const handleUpdateSection = useCallback(
    (sectionId: string, patch: Partial<FormSection>) => {
      sectionAutosave.queue((def) => ({
        ...def,
        sections: def.sections.map((s) =>
          s.id === sectionId ? { ...s, ...patch } : s,
        ),
      }));
    },
    [sectionAutosave],
  );

  const handleAddSection = useCallback(() => {
    sectionAutosave.queue((def) => ({
      ...def,
      sections: [
        ...def.sections,
        {
          id: crypto.randomUUID(),
          heading: "New section",
          description: null,
          fields: [],
        },
      ],
    }));
    sectionAutosave.flush();
  }, [sectionAutosave]);

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      // formDefinitionSchema requires sections.min(1) — guard.
      if (definition.sections.length <= 1) return;
      sectionAutosave.queue((def) => ({
        ...def,
        sections: def.sections.filter((s) => s.id !== sectionId),
      }));
      sectionAutosave.flush();
    },
    [definition.sections.length, sectionAutosave],
  );

  // ----- DnD --------------------------------------------------------------

  const sensors = useSortableSensors();

  function onDragEnd(e: DragEndEvent) {
    const activeId = e.active.id as string;
    const overId = e.over?.id as string | undefined;
    if (!overId || activeId === overId) return;

    let section: FormSection | undefined;
    for (const s of definition.sections) {
      if (s.fields.some((f) => f.id === activeId)) {
        section = s;
        break;
      }
    }
    if (!section) return;
    const fromIdx = section.fields.findIndex((f) => f.id === activeId);
    const toIdx = section.fields.findIndex((f) => f.id === overId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = arrayMove(section.fields, fromIdx, toIdx);
    // Build the full ordered tree for the PATCH — every section's ordering
    // is sent (idempotent + re-dispatchable on failure).
    const sections = definition.sections.map((s) =>
      s.id === section!.id
        ? { id: s.id, fieldIds: reordered.map((f) => f.id) }
        : { id: s.id, fieldIds: s.fields.map((f) => f.id) },
    );
    reorderFields.mutate({ formId: form.id, sections });
  }

  return (
    <div className="grid gap-6 lg:grid-cols-10">
      {/* LEFT — canvas */}
      <div className="space-y-4 lg:col-span-7">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          {definition.sections.map((section, idx) => (
            <SortableContext
              key={section.id}
              items={section.fields.map((f) => f.id)}
              strategy={verticalListSortingStrategy}
            >
              <SectionBlock
                section={section}
                sectionIndex={idx}
                canDelete={definition.sections.length > 1}
                selectedFieldId={selectedFieldId}
                onSelectField={setSelectedFieldId}
                onUpdateSection={(patch) => handleUpdateSection(section.id, patch)}
                onDeleteSection={() => handleDeleteSection(section.id)}
                onAddField={(kind, insertAfter) =>
                  handleAddField(section.id, kind, insertAfter)
                }
              />
            </SortableContext>
          ))}
        </DndContext>

        <Button variant="outline" size="sm" onClick={handleAddSection}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Add section
        </Button>
      </div>

      {/* RIGHT — config panel */}
      <div className="lg:col-span-3">
        <div className="sticky top-4">
          {selectedField && selectedSectionId ? (
            <FieldConfigPanel
              key={selectedField.id}
              field={selectedField}
              onPatch={(patch) => handlePatchField(selectedField.id, patch)}
              onDelete={() => handleDeleteField(selectedField.id)}
              onAddBelow={(kind) =>
                handleAddField(selectedSectionId, kind, selectedField.id)
              }
            />
          ) : (
            <EmptyPanel />
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyPanel() {
  return (
    <div className="rounded-xl border border-dashed bg-card/40 p-6 text-center">
      <p className="text-sm text-muted-foreground">
        Select a field to edit its label, validation, and CRM mapping.
      </p>
      <p className="mt-2 text-xs text-muted-foreground/70">
        Click any field on the left, or use the{" "}
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-muted">
          <Plus className="h-3 w-3" />
        </span>{" "}
        button to add a new one.
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Field factory — minimal schema-valid FormField per kind, unique machine
// name within the definition.
// ----------------------------------------------------------------------------

function makeField(kind: FormFieldKind, def: FormDefinition): FormField {
  const id = crypto.randomUUID();
  const base = {
    id,
    name: uniqueName(kind, def),
    label: defaultLabel(kind),
    required: false as const,
  };
  switch (kind) {
    case "text":
      return { kind: "text", ...base };
    case "long_text":
      return { kind: "long_text", ...base };
    case "email":
      return { kind: "email", ...base };
    case "number":
      return { kind: "number", ...base, integer: true };
    case "date":
      return { kind: "date", ...base };
    case "partner_lookup":
      return { kind: "partner_lookup", ...base };
    case "section_heading":
      return { kind: "section_heading", ...base };
    case "file":
      return { kind: "file", ...base };
    case "select":
      return {
        kind: "select",
        ...base,
        multiple: false,
        options: [
          { value: "option_1", label: "Option 1" },
          { value: "option_2", label: "Option 2" },
        ],
      };
    case "checkbox":
      return { kind: "checkbox", ...base };
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      throw new Error(`Unknown field kind: ${kind as string}`);
    }
  }
}

function defaultLabel(kind: FormFieldKind): string {
  switch (kind) {
    case "text":
      return "Text field";
    case "long_text":
      return "Long text";
    case "email":
      return "Email";
    case "number":
      return "Number";
    case "date":
      return "Date";
    case "partner_lookup":
      return "Partner";
    case "section_heading":
      return "Section heading";
    case "file":
      return "File upload";
    case "select":
      return "Select";
    case "checkbox":
      return "Checkbox";
  }
}

function uniqueName(kind: FormFieldKind, def: FormDefinition): string {
  const taken = new Set<string>();
  for (const s of def.sections) {
    for (const f of s.fields) taken.add(f.name);
  }
  let i = 1;
  let candidate = `${kind}_${i}`;
  while (taken.has(candidate)) {
    i += 1;
    candidate = `${kind}_${i}`;
  }
  return candidate;
}

function findField(def: FormDefinition, fieldId: string): FormField | null {
  for (const section of def.sections) {
    const found = section.fields.find((f) => f.id === fieldId);
    if (found) return found;
  }
  return null;
}
